// RaphaRegister — mobile-first QR signup for the Korea store.
// Customer scans a QR code, enters name + email, gets auto-logged-in as a
// guest user. No password, no queue — just register and walk to a station.

import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  CssBaseline,
  Stack,
  TextField,
  ThemeProvider,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import Brand from '../components/Brand';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { raphaTheme, colors, shadows } from '../theme';

export default function RaphaRegister() {
  const { t } = useTranslation('rapha');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setErrorMsg(null);

    if (!name || !email) {
      setErrorMsg(t('register.errorMissing'));
      return;
    }

    setLoading(true);
    fetch(`${process.env.REACT_APP_API}/event/guest`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, email, source: 'rapha_korea_2026' }),
    })
      .then((r) => r.json())
      .then((data) => {
        if (data.statusCode && data.statusCode > 201)
          throw new Error(data.message || 'Registration failed');
        localStorage.setItem(
          process.env.REACT_APP_TOKEN_KEY || '',
          data.accessToken
        );
        localStorage.setItem('rapha_guest_name', name);
        window.location.href = '/rapha/welcome';
      })
      .catch((e) => {
        setErrorMsg(e.message || 'Something went wrong.');
        setLoading(false);
      });
  };

  return (
    <ThemeProvider theme={raphaTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 6,
          px: 2,
          backgroundImage: colors.gradHero,
        }}
      >
        <Container maxWidth="sm" sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <Box sx={{ position: 'absolute', top: 24, right: 24 }}>
            <LanguageSwitcher />
          </Box>
          <Box sx={{ mb: 5 }}>
            <Brand size="lg" href="/" subtitle="× Rapha" />
          </Box>

          <Box
            sx={{
              width: '100%',
              maxWidth: 440,
              p: { xs: 3, sm: 5 },
              backgroundColor: 'rgba(18, 20, 28, 0.8)',
              backdropFilter: 'blur(20px)',
              WebkitBackdropFilter: 'blur(20px)',
              boxShadow: shadows.xl,
              border: `1px solid ${colors.border}`,
              borderRadius: 3,
            }}
          >
            <Typography variant="h4" sx={{ mb: 1, fontWeight: 700 }}>
              {t('register.title')}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
              {t('register.subtitle')}
            </Typography>

            {errorMsg && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {errorMsg}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Stack spacing={2.5}>
                <TextField
                  fullWidth
                  label={t('register.name')}
                  autoComplete="name"
                  autoFocus
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
                <TextField
                  fullWidth
                  label={t('register.email')}
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  size="large"
                  disabled={loading}
                  sx={{ py: 1.5, fontSize: '1rem' }}
                >
                  {loading ? (
                    <CircularProgress size={22} sx={{ color: colors.textInverse }} />
                  ) : (
                    t('register.submit')
                  )}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Container>
      </Box>
    </ThemeProvider>
  );
}
